import React from 'react';
import { Eye, GripVertical } from 'lucide-react';
import { InlineLoadingState, LoadingSpinner } from '../../../../components/LoadingSpinner';
import type {
  DocumentEmailPlaceholderKey,
  DocumentEmailTemplateValues,
  EmailTemplateDocumentType,
} from '../../utils/documentEmailTemplates';
import {
  applyDocumentEmailBodyTemplate,
  applyDocumentEmailSubjectTemplate,
  findUnknownDocumentEmailPlaceholders,
  normalizeDocumentEmailPlaceholders,
  resolveDocumentEmailPlaceholderKey,
} from '../../utils/documentEmailTemplates';
import {
  fetchDocumentEmailTemplate,
  saveDocumentEmailTemplate,
} from '../../utils/documentEmailTemplateApi';
import { SettingsCard } from '../../components/SettingsCard';
import { SettingsCardSavedStatus } from '../../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../../utils/settingsSectionStyles';
import {
  DOCUMENT_EMAIL_PLACEHOLDER_INFO,
  DOCUMENT_EMAIL_TEMPLATE_UI,
  documentEmailPlaceholderName,
} from './documentEmailTemplateUi';
import {
  PLACEHOLDER_BODY_ONLY_DRAG_TYPE,
  PLACEHOLDER_DRAG_TYPE,
  PlaceholderTemplateEditor,
  startPlaceholderDrag,
  type PlaceholderTemplateEditorHandle,
  type PlaceholderTemplateToken,
} from './PlaceholderTemplateEditor';

type Props = {
  documentType: EmailTemplateDocumentType;
};

type TemplateField = 'subject' | 'body';

const PLACEHOLDER_CHIP_CLASS =
  'inline-flex h-7 cursor-grab items-center gap-1 rounded-md border border-app-border bg-app-surface px-2 text-xs font-medium text-app-text-muted transition-colors hover:border-app-border-strong hover:bg-app-hover active:cursor-grabbing';

const FIELD_DROP_ACTIVE_CLASS = 'ring-2 ring-pipedrive-blue/30 border-pipedrive-blue';

function placeholderToken(key: DocumentEmailPlaceholderKey): PlaceholderTemplateToken {
  return { name: documentEmailPlaceholderName(key), label: DOCUMENT_EMAIL_PLACEHOLDER_INFO[key].label };
}

export function DocumentEmailTemplateSettingsCard({ documentType }: Props) {
  const ui = DOCUMENT_EMAIL_TEMPLATE_UI[documentType];
  const fieldId = React.useId();
  const subjectLabelId = `${fieldId}-subject-label`;
  const bodyLabelId = `${fieldId}-body-label`;

  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [activeField, setActiveField] = React.useState<TemplateField>('body');
  /** Gerade gezogener Platzhalter — hebt die moeglichen Zielfelder hervor. */
  const [draggedPlaceholder, setDraggedPlaceholder] = React.useState<DocumentEmailPlaceholderKey | null>(
    null,
  );
  const [form, setForm] = React.useState({
    subjectTemplate: ui.defaultSubjectTemplate,
    bodyTemplate: ui.defaultBodyTemplate,
  });
  /** Gespeicherter Stand — Basis fuer „ungespeicherte Aenderungen“. */
  const [baseline, setBaseline] = React.useState(form);

  const subjectEditorRef = React.useRef<PlaceholderTemplateEditorHandle | null>(null);
  const bodyEditorRef = React.useRef<PlaceholderTemplateEditorHandle | null>(null);

  const load = React.useCallback(() => {
    setLoaded(false);
    setError(null);
    fetchDocumentEmailTemplate(documentType)
      .then((data) => {
        const pick = (stored: unknown, fallback: string) =>
          typeof stored === 'string' && stored.trim().length > 0
            ? normalizeDocumentEmailPlaceholders(stored)
            : fallback;
        const next = {
          subjectTemplate: pick(data.subjectTemplate, ui.defaultSubjectTemplate),
          bodyTemplate: pick(data.bodyTemplate, ui.defaultBodyTemplate),
        };
        setForm(next);
        setBaseline(next);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Vorlage konnte nicht geladen werden.');
      })
      .finally(() => setLoaded(true));
  }, [documentType, ui.defaultBodyTemplate, ui.defaultSubjectTemplate]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const dirty =
    form.subjectTemplate !== baseline.subjectTemplate || form.bodyTemplate !== baseline.bodyTemplate;
  const isDefault =
    form.subjectTemplate === ui.defaultSubjectTemplate && form.bodyTemplate === ui.defaultBodyTemplate;

  const updateForm = (patch: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  /** Im Betreff gibt es keine Anrede — dort bleibt der Name normaler Text. */
  const resolveTokenForField = React.useCallback(
    (field: TemplateField) => (name: string): PlaceholderTemplateToken | null => {
      const key = resolveDocumentEmailPlaceholderKey(name);
      if (!key) return null;
      if (field === 'subject' && DOCUMENT_EMAIL_PLACEHOLDER_INFO[key].bodyOnly) return null;
      return placeholderToken(key);
    },
    [],
  );
  const resolveSubjectToken = React.useMemo(
    () => resolveTokenForField('subject'),
    [resolveTokenForField],
  );
  const resolveBodyToken = React.useMemo(() => resolveTokenForField('body'), [resolveTokenForField]);

  const insertPlaceholder = (key: DocumentEmailPlaceholderKey) => {
    const bodyOnly = DOCUMENT_EMAIL_PLACEHOLDER_INFO[key].bodyOnly === true;
    const field: TemplateField = bodyOnly ? 'body' : activeField;
    setActiveField(field);
    const editor = field === 'subject' ? subjectEditorRef.current : bodyEditorRef.current;
    editor?.insertToken(placeholderToken(key));
  };

  const handleDragOver = (field: TemplateField) => (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes(PLACEHOLDER_DRAG_TYPE)) return;
    const bodyOnly = e.dataTransfer.types.includes(PLACEHOLDER_BODY_ONLY_DRAG_TYPE);
    e.preventDefault();
    e.dataTransfer.dropEffect = bodyOnly && field === 'subject' ? 'none' : 'copy';
  };

  const handleTokenDrop =
    (field: TemplateField) =>
    (e: React.DragEvent<HTMLDivElement>): PlaceholderTemplateToken | null => {
      setDraggedPlaceholder(null);
      const name = e.dataTransfer.getData(PLACEHOLDER_DRAG_TYPE);
      if (!name) return null;
      const key = resolveDocumentEmailPlaceholderKey(name);
      if (!key) return null;
      if (field === 'subject' && DOCUMENT_EMAIL_PLACEHOLDER_INFO[key].bodyOnly) return null;
      setActiveField(field);
      return placeholderToken(key);
    };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const data = await saveDocumentEmailTemplate(documentType, form);
      const next = {
        subjectTemplate: data.subjectTemplate || form.subjectTemplate,
        bodyTemplate: data.bodyTemplate || form.bodyTemplate,
      };
      setForm(next);
      setBaseline(next);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const previewValues = React.useMemo<DocumentEmailTemplateValues>(
    () => ({
      documentReference: 'AB-2026-0042',
      dateLabel: new Date().toLocaleDateString('de-DE'),
      customerName: 'Muster GmbH',
      signerName: 'Herr Müller',
    }),
    [],
  );

  const draggedIsBodyOnly =
    draggedPlaceholder != null && DOCUMENT_EMAIL_PLACEHOLDER_INFO[draggedPlaceholder].bodyOnly === true;

  const unknownPlaceholders = [
    ...findUnknownDocumentEmailPlaceholders(form.subjectTemplate, { allowGreeting: false }).map(
      (token) => `${token} (Betreff)`,
    ),
    ...findUnknownDocumentEmailPlaceholders(form.bodyTemplate, { allowGreeting: true }).map(
      (token) => `${token} (E-Mail-Text)`,
    ),
  ];

  return (
    <SettingsCard title={ui.title} description={ui.description}>
      {!loaded ? (
        <InlineLoadingState label="Lade Vorlage…" className="justify-start" />
      ) : (
        <div className="space-y-4">
          <div>
            <span id={subjectLabelId} className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              Betreff
            </span>
            <PlaceholderTemplateEditor
              ref={subjectEditorRef}
              value={form.subjectTemplate}
              onChange={(subjectTemplate) => updateForm({ subjectTemplate })}
              resolveToken={resolveSubjectToken}
              ariaLabelledBy={subjectLabelId}
              onFocus={() => setActiveField('subject')}
              onDragOver={handleDragOver('subject')}
              onTokenDrop={handleTokenDrop('subject')}
              // Zeilenhoehe = Inhaltshoehe von `h-9` (36px minus 2px Rahmen), damit
              // der Text mittig sitzt und nichts ueberlaeuft.
              className={`${SETTINGS_INPUT_CLASS} leading-[2.125rem] ${
                draggedPlaceholder != null && !draggedIsBodyOnly ? FIELD_DROP_ACTIVE_CLASS : ''
              }`}
            />
          </div>
          <div>
            <span id={bodyLabelId} className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              E-Mail-Text
            </span>
            <PlaceholderTemplateEditor
              ref={bodyEditorRef}
              value={form.bodyTemplate}
              onChange={(bodyTemplate) => updateForm({ bodyTemplate })}
              resolveToken={resolveBodyToken}
              ariaLabelledBy={bodyLabelId}
              multiline
              onFocus={() => setActiveField('body')}
              onDragOver={handleDragOver('body')}
              onTokenDrop={handleTokenDrop('body')}
              className={`${SETTINGS_TEXTAREA_CLASS} min-h-[11rem] ${
                draggedPlaceholder != null ? FIELD_DROP_ACTIVE_CLASS : ''
              }`}
            />
          </div>

          <div>
            <p className={`${SETTINGS_FIELD_LABEL} mb-1.5`}>Platzhalter einfügen</p>
            <div className="flex flex-wrap gap-1.5">
              {ui.placeholders.map((key) => {
                const info = DOCUMENT_EMAIL_PLACEHOLDER_INFO[key];
                return (
                  <button
                    key={key}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      startPlaceholderDrag(e, placeholderToken(key), { bodyOnly: info.bodyOnly });
                      setDraggedPlaceholder(key);
                    }}
                    onDragEnd={() => setDraggedPlaceholder(null)}
                    onClick={() => insertPlaceholder(key)}
                    className={PLACEHOLDER_CHIP_CLASS}
                    title={`${info.hint} — an die gewünschte Stelle ziehen oder klicken`}
                  >
                    <GripVertical className="w-3 h-3 text-slate-400" strokeWidth={2} />
                    {info.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-app-text-subtle">
              An die gewünschte Stelle ziehen oder klicken — beim Klick landet der Platzhalter an der
              Cursorposition im {activeField === 'subject' ? 'Betreff' : 'E-Mail-Text'}. Beim Versand wird
              daraus der echte Wert. Die Anrede gibt es nur im E-Mail-Text.
            </p>
          </div>

          {unknownPlaceholders.length > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Diese Platzhalter kennt der Versand nicht, sie erscheinen wörtlich in der E-Mail:{' '}
              {unknownPlaceholders.join(', ')}
            </div>
          ) : null}

          {previewOpen ? (
            <div className="rounded-md border border-app-border bg-app-surface-muted px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className={SETTINGS_FIELD_LABEL}>Vorschau mit Beispieldaten</p>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="text-xs font-medium text-pipedrive-blue hover:underline"
                  aria-expanded
                >
                  Ausblenden
                </button>
              </div>
              <div className="mt-2 space-y-2">
                <p className="text-sm font-medium text-app-text">
                  {applyDocumentEmailSubjectTemplate(documentType, form.subjectTemplate, previewValues)}
                </p>
                <p className="whitespace-pre-line text-sm text-app-text-muted">
                  {applyDocumentEmailBodyTemplate(documentType, form.bodyTemplate, previewValues)}
                </p>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-pipedrive-blue hover:underline"
              aria-expanded={false}
            >
              <Eye className="w-3.5 h-3.5" strokeWidth={1.75} />
              Vorschau mit Beispieldaten anzeigen
            </button>
          )}

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !dirty}
              className={SETTINGS_BTN_PRIMARY}
            >
              {saving ? (
                <>
                  <LoadingSpinner />
                  Speichert…
                </>
              ) : (
                'Vorlage speichern'
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                updateForm({
                  subjectTemplate: ui.defaultSubjectTemplate,
                  bodyTemplate: ui.defaultBodyTemplate,
                })
              }
              disabled={saving || isDefault}
              className={SETTINGS_BTN_OUTLINE}
              title="Betreff und Text auf die Standardvorlage zurücksetzen"
            >
              Auf Standard zurücksetzen
            </button>
            <span aria-live="polite" className="text-sm">
              {dirty ? <span className="text-app-text-subtle">Ungespeicherte Änderungen</span> : null}
            </span>
            <SettingsCardSavedStatus saved={!dirty && saved}>Gespeichert</SettingsCardSavedStatus>
          </div>
        </div>
      )}
    </SettingsCard>
  );
}
